import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft, ArrowRight, PlayCircle, FileText, Download } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api, API_BASE } from "@/lib/api";
import LockedContent from "@/pages/LockedContent";

function fmtBytes(n) {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function AttachmentsPanel({ lesson }) {
    const attachments = lesson.attachments || [];
    if (attachments.length === 0) return null;
    return (
        <div className="mt-8 st-surface p-6" data-testid="lesson-attachments-panel">
            <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                Attachments
            </div>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 p-3 rounded"
                        style={{ background: "var(--st-white)", border: "1px solid var(--st-line)" }}
                        data-testid={`lesson-attachment-${a.id}`}>
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
                             style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}>
                            <FileText size={18} color="var(--st-royal)" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold" style={{ color: "var(--st-navy)" }} title={a.filename}>
                                {a.filename}
                            </div>
                            <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                                {fmtBytes(a.size_bytes)} · PDF
                            </div>
                        </div>
                        <a href={`${API_BASE}/curriculum/lessons/${lesson.id}/attachments/${a.id}`}
                           className="btn-outline-navy text-xs inline-flex items-center gap-1 flex-shrink-0"
                           data-testid={`lesson-attachment-download-${a.id}`}>
                            <Download size={12} /> Download
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Lesson viewer — video iframe (YouTube gets progress tracking via the IFrame API;
 * other providers get a manual "Mark complete" button). Reading content shown below.
 */

function extractYouTubeId(url) {
    if (!url) return null;
    const m = url.match(/(?:embed\/|v=|be\/)([\w-]{11})/);
    return m ? m[1] : null;
}

function VideoPlayer({ lesson, onProgress }) {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const pollRef = useRef(null);

    useEffect(() => {
        if (lesson.video_provider !== "youtube") return;
        const videoId = extractYouTubeId(lesson.video_url);
        if (!videoId) return;

        function initPlayer() {
            if (playerRef.current || !containerRef.current) return;
            // eslint-disable-next-line no-undef
            playerRef.current = new window.YT.Player(containerRef.current, {
                videoId,
                playerVars: { rel: 0, modestbranding: 1 },
                events: {
                    onStateChange: (e) => {
                        // 1 = playing
                        if (e.data === 1 && !pollRef.current) {
                            pollRef.current = setInterval(() => {
                                try {
                                    const secs = playerRef.current.getCurrentTime() || 0;
                                    const dur = playerRef.current.getDuration() || 0;
                                    if (dur > 0) {
                                        onProgress(Math.floor(secs), Math.min(secs / dur, 1));
                                    }
                                } catch { /* ignore */ }
                            }, 5000);
                        } else if (e.data !== 1 && pollRef.current) {
                            clearInterval(pollRef.current);
                            pollRef.current = null;
                            // Send one last snapshot when paused.
                            try {
                                const secs = playerRef.current.getCurrentTime() || 0;
                                const dur = playerRef.current.getDuration() || 0;
                                if (dur > 0) onProgress(Math.floor(secs), Math.min(secs / dur, 1));
                            } catch { /* ignore */ }
                        }
                    },
                },
            });
        }

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            // Load YouTube IFrame API once
            if (!document.querySelector("script[data-yt-api]")) {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/iframe_api";
                tag.setAttribute("data-yt-api", "true");
                document.body.appendChild(tag);
            }
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (typeof prev === "function") prev();
                initPlayer();
            };
        }

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (playerRef.current?.destroy) {
                try { playerRef.current.destroy(); } catch { /* ignore */ }
                playerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lesson.id]);

    if (lesson.video_provider === "youtube") {
        return (
            <div
                className="aspect-video w-full rounded-lg overflow-hidden"
                style={{ background: "#000" }}
                data-testid="lesson-video-container"
            >
                <div ref={containerRef} className="w-full h-full" />
            </div>
        );
    }
    // Fallback iframe (Vimeo/other) — no automatic progress tracking.
    return (
        <div className="aspect-video w-full rounded-lg overflow-hidden" style={{ background: "#000" }} data-testid="lesson-video-container">
            <iframe
                src={lesson.video_url}
                title={lesson.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    );
}

export default function LessonPage() {
    const { lessonId } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "loading" });
    const [markBusy, setMarkBusy] = useState(false);
    const inFlight = useRef(0);

    async function load() {
        try {
            const lesson = await api.getLesson(lessonId);
            setState({ status: "ok", lesson });
        } catch (err) {
            if (err.status === 423) {
                setState({ status: "locked", detail: err.data?.detail || err.data });
                return;
            }
            if (err.status === 402) {
                setState({ status: "inactive" });
                return;
            }
            setState({ status: "error", message: err.message });
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [lessonId]);

    async function handleProgress(watched_seconds, watched_percent) {
        // Debounce concurrent posts.
        const my = ++inFlight.current;
        try {
            const res = await api.postLessonProgress(lessonId, watched_seconds, watched_percent);
            if (my !== inFlight.current) return;
            setState((s) => s.status === "ok" ? {
                ...s,
                lesson: { ...s.lesson, progress: { ...s.lesson.progress, ...res.progress } },
            } : s);
        } catch { /* ignore transient errors */ }
    }

    async function handleMarkComplete() {
        setMarkBusy(true);
        try {
            const res = await api.markLessonComplete(lessonId);
            setState((s) => ({ ...s, lesson: { ...s.lesson, progress: { ...s.lesson.progress, ...res.progress } } }));
        } catch (err) {
            alert(err.message || "Unable to mark complete.");
        } finally {
            setMarkBusy(false);
        }
    }

    if (state.status === "loading") {
        return <PageShell><div className="max-w-5xl mx-auto px-6 py-16" data-testid="lesson-loading">Loading lesson…</div></PageShell>;
    }
    if (state.status === "locked") {
        return <PageShell><LockedContent detail={state.detail} /></PageShell>;
    }
    if (state.status === "inactive") {
        return (
            <PageShell>
                <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="lesson-inactive">
                    <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>Subscription required</h1>
                    <p className="mt-4" style={{ color: "var(--st-ink-muted)" }}>Reactivate your subscription to resume the curriculum.</p>
                    <Link to="/dashboard" className="btn-royal text-sm mt-6 inline-block">Return to dashboard</Link>
                </div>
            </PageShell>
        );
    }
    if (state.status === "error") {
        return <PageShell><div className="max-w-3xl mx-auto px-6 py-16" data-testid="lesson-error"><p style={{ color: "#B42318" }}>{state.message}</p></div></PageShell>;
    }

    const { lesson } = state;
    const completed = !!lesson.progress?.completed;
    const pct = Math.round((lesson.progress?.watched_percent || 0) * 100);

    return (
        <PageShell>
            <section className="max-w-5xl mx-auto px-6 py-10" data-testid="lesson-page">
                <div className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--st-silver-500)" }}>
                    <Link to="/dashboard" className="hover:underline flex items-center gap-1">
                        <ArrowLeft size={12} strokeWidth={2} /> Back to curriculum
                    </Link>
                    <span className="st-divider-dot" />
                    <span className="tracking-caps font-semibold" style={{ color: "var(--st-royal)" }}>
                        {lesson.phase.name} · Module {lesson.module.order}
                    </span>
                </div>

                <h1 className="font-serif text-3xl md:text-4xl" style={{ color: "var(--st-navy)" }} data-testid="lesson-title">
                    {lesson.title}
                </h1>

                {lesson.video_url && (
                    <div className="mt-6">
                        <VideoPlayer lesson={lesson} onProgress={handleProgress} />
                    </div>
                )}

                {/* Progress + mark complete */}
                <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="lesson-progress-row">
                    <div className="flex-1 min-w-[200px]">
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--st-silver-100)" }}>
                            <div className="h-full rounded-full"
                                 style={{ width: `${pct}%`, background: completed ? "#067647" : "var(--st-royal)", transition: "width 0.3s ease" }}
                                 data-testid="lesson-progress-bar" />
                        </div>
                        <div className="mt-1 text-xs" style={{ color: "var(--st-ink-muted)" }}>
                            {completed ? "Completed" : `${pct}% watched`}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleMarkComplete}
                        disabled={markBusy || completed}
                        className={completed ? "btn-ghost text-sm inline-flex items-center gap-2" : "btn-outline-navy text-sm inline-flex items-center gap-2"}
                        data-testid="lesson-mark-complete-btn"
                    >
                        {completed ? <CheckCircle2 size={14} strokeWidth={2} /> : <PlayCircle size={14} strokeWidth={2} />}
                        {completed ? "Completed" : markBusy ? "Marking…" : "Mark as complete"}
                    </button>
                </div>

                {/* Reading */}
                {lesson.reading_content && (
                    <div className="mt-10 st-surface p-6" data-testid="lesson-reading-content">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                            Reading
                        </div>
                        <p className="mt-2 text-base leading-relaxed" style={{ color: "var(--st-ink)" }}>
                            {lesson.reading_content}
                        </p>
                    </div>
                )}

                <AttachmentsPanel lesson={lesson} />

                <div className="mt-10 flex justify-between">
                    <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="btn-ghost text-sm inline-flex items-center gap-2"
                        data-testid="lesson-back-btn"
                    >
                        <ArrowLeft size={14} strokeWidth={2} /> Curriculum
                    </button>
                </div>
            </section>
        </PageShell>
    );
}
